import { DateOnly } from '@if/core/date'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  FormMethods,
  useFormDialog,
  useIfUser,
} from '@if/mui'
import { Stack } from '@mui/material'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { DEFAULT_PAYMENT_METHOD, EPaymentMethod } from '@src/types/constants'
import { Plus } from 'lucide-react'
import { JSX, useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import ReturnToCalendarAction, { RETURN_TO_CALENDAR, ToolResult } from '../EditPayments-tool'
import { useAddPaymentSchema } from './LoanAddPayment-schema'

interface LoanAddPaymentBodyProps {
  form: FormMethods<EditPaymentSchemaType>
  paymentMethodOptions: JSX.Element[]
  loanActivationDate: DateOnly
}

const LoanAddPaymentBody = ({ form, paymentMethodOptions, loanActivationDate }: LoanAddPaymentBodyProps) => {
  const { t } = useTranslation()
  const { control, setValue } = form
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments

  const paymentMethod = useWatch({ control, name: 'paymentMethod' })

  // Only a pre-authorized payment can carry an SMS reminder, so changing the method drops one
  // the agent had already ticked rather than leaving an unreachable value on the row.
  const handlePaymentMethodChange = useCallback(() => {
    setValue('sendSmsReminder', false, { shouldValidate: true })
  }, [setValue])

  const minStartDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(paymentMethod, loanActivationDate, canBypassAcpLock),
    [paymentMethod, canBypassAcpLock, loanActivationDate],
  )

  return (
    <Stack spacing={1}>
      <ControlledNumberField control={control} name="amount" isMoney label={t('loan.payment.amount')} autoFocus />
      <ControlledDateOnlyPicker
        control={control}
        name="effectiveDate"
        label={t('loan.payment.date')}
        minDate={minStartDate}
      />
      <ControlledSelect
        control={control}
        name="paymentMethod"
        options={paymentMethodOptions}
        label={t('loan.paymentMethod')}
        disableEmptyOption
        onChange={handlePaymentMethodChange}
      />
      <ControlledTextField control={control} name="note" label={t('loan.payment.description')} />
      <ControlledCheckbox
        control={control}
        name="sendSmsReminder"
        label={t('loan.payment.sendSMSReminder')}
        disabled={paymentMethod !== EPaymentMethod.PreAuthorizedDebit}
      />
    </Stack>
  )
}

interface UseLoanAddPaymentDialogProps {
  loanActivationDate: DateOnly
  paymentMethodOptions: JSX.Element[]
  initialAmount?: number
}

/** Stages one new payment on the editor's table. */
export const useLoanAddPaymentDialog = ({
  loanActivationDate,
  paymentMethodOptions,
  initialAmount,
}: UseLoanAddPaymentDialogProps) => {
  const { t } = useTranslation()
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  const schema = useAddPaymentSchema(loanActivationDate, canBypassAcpLock)

  return useFormDialog<EditPaymentSchemaType, void, ToolResult<EditPaymentSchemaType>>({
    icon: Plus,
    title: t('loan.payment.add'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    // The id is minted per open — two runs of the tool must not stage the same row twice.
    defaultValues: () => ({
      id: crypto.randomUUID(),
      amount: initialAmount ?? 0,
      paymentMethod: DEFAULT_PAYMENT_METHOD,
      sendSmsReminder: true,
    }),
    successMessage: false,
    additionalActions: (ctx) => <ReturnToCalendarAction onReturn={() => ctx.close(RETURN_TO_CALENDAR)} />,
    content: ({ form }) => (
      <LoanAddPaymentBody
        form={form}
        paymentMethodOptions={paymentMethodOptions}
        loanActivationDate={loanActivationDate}
      />
    ),
    onSubmit: (data) => data,
  })
}
